/**
 * T521 — Date/time formatting in Europe/Berlin.
 *
 * Server stores everything in `timestamptz` (correct), but browsers render
 * dates in the client locale by default. For German users + accounting
 * workflows that's wrong — a 2026-05-01T00:30:00Z timestamp must show as
 * "01.05.2026, 02:30" (CEST), not "30.04.2026" (UTC-rendered).
 *
 * All date helpers here force Europe/Berlin so the UI is consistent across
 * VPS-deployed and locally-developed sessions, and audit stamps stay
 * comparable regardless of where the user is physically located.
 */

const ZONE = 'Europe/Berlin';

const FULL = new Intl.DateTimeFormat('de-DE', {
  timeZone: ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const DATE_ONLY = new Intl.DateTimeFormat('de-DE', {
  timeZone: ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * Format an ISO timestamp or `Date` as `DD.MM.YYYY, HH:mm` in Berlin time.
 * Returns the empty string for invalid / falsy input rather than throwing —
 * call sites expect to render `—` for "no date".
 */
export function formatDateTimeBerlin(input: string | Date | null | undefined): string {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return FULL.format(d);
}

/**
 * Format an ISO date (`YYYY-MM-DD`) or `Date` as `DD.MM.YYYY` in Berlin
 * time. The string overload is the common case — `expense.invoiceDate`
 * is already a date-only string; we just reformat without changing the
 * underlying day (the input has no time-of-day to convert).
 */
export function formatDateBerlin(input: string | Date | null | undefined): string {
  if (!input) return '';
  if (typeof input === 'string') {
    // Pure date-only string (`YYYY-MM-DD`)? Parse as Berlin-noon so DST
    // boundaries never shift the calendar day on the round-trip.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
    if (m) {
      // Construct a Date that represents "this calendar day at noon UTC".
      // Berlin is UTC+1/+2 → noon UTC is always the same calendar day in
      // Berlin, regardless of DST.
      const d = new Date(`${input}T12:00:00Z`);
      return DATE_ONLY.format(d);
    }
  }
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return DATE_ONLY.format(d);
}

/**
 * Today's calendar date in Berlin as `YYYY-MM-DD`. Use for default-date
 * fields on new expenses so a 23:30-UTC upload doesn't backdate the
 * Belegdatum to "yesterday" in the user's perception.
 */
export function todayInBerlin(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
